import { Link } from 'react-router-dom';
import { itemTextLang } from '@/features/publicsite/contentLanguage';
import { cardField, cardImageSrc } from '@/features/publicsite/home/homeSection';
import type { FocusCard } from '@/features/publicsite/types/home/FocusCard';
import type { HomeFocusProps } from '@/features/publicsite/types/home/HomeFocusProps';
import { useLanguage } from '@/i18n/LanguageContext';
import { publicHome } from '@/i18n/dictionaries/publicHome';
import { useTranslation } from '@/i18n/useTranslation';

const FOCUS_CARDS: FocusCard[] = [
  {
    to: '/biblioteca',
    image: 'feat-orchard',
    category: 'focusLibraryCat',
    title: 'focusLibraryTitle',
    cta: 'focusLibraryCta',
  },
  {
    to: '/finantare',
    image: 'feat-irrigation',
    category: 'focusFundingCat',
    title: 'focusFundingTitle',
    cta: 'focusFundingCta',
  },
  {
    to: '/instruiri',
    image: 'feat-greenhouse',
    category: 'focusTrainingCat',
    title: 'focusTrainingTitle',
    cta: 'focusTrainingCta',
  },
];

/**
 * "Agriculture in focus" — the heading and three editorial cards, administered from
 * /app/admin/acasa (V108). Until the section arrives, and when it cannot, the dictionary's texts and
 * the static pictures stand in: the same cards in the same boxes, so the answer changes words and
 * pictures, never the layout.
 */
export function HomeFocus({ home }: HomeFocusProps) {
  const { t } = useTranslation(publicHome);
  const { language } = useLanguage();
  const lang = (field: string) => (home ? itemTextLang(home, field, language) : undefined);

  return (
    <section className="section">
      <div className="container">
        <div className="section-head">
          <div>
            <span className="eyebrow" lang={lang('eyebrow')}>
              {home ? home.eyebrow : t('focusEyebrow')}
            </span>
            <h2 lang={lang('title')}>{home ? home.title : t('focusHeading')}</h2>
            <p lang={lang('lead')}>{home ? home.lead : t('focusLead')}</p>
          </div>
        </div>
        <div className="section-body feature-grid">
          {FOCUS_CARDS.map((card, index) => {
            const served = home?.cards[index];
            return (
              <Link className="news-story" to={served?.linkPath ?? card.to} key={card.image}>
                <img
                  className="news-story__img"
                  src={cardImageSrc(served, `/img/news/${card.image}.jpg`)}
                  alt=""
                  aria-hidden="true"
                />
                <span className="news-story__content">
                  <span className="news-card__cat" lang={lang(cardField(index, 'label'))}>
                    {served ? served.label : t(card.category)}
                  </span>
                  <span className="news-story__title" lang={lang(cardField(index, 'title'))}>
                    {served ? served.title : t(card.title)}
                  </span>
                  <span className="news-story__date" lang={lang(cardField(index, 'cta'))}>
                    {served ? served.cta : t(card.cta)}
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
